import {
  ClickAwayListener,
  FormHelperText,
  Grid,
  Input,
  MenuItem,
  MenuList,
  Paper,
} from '@material-ui/core';
import { AsYouType } from 'libphonenumber-js';
import head from 'lodash/head';
import reduce from 'lodash/reduce';
import tail from 'lodash/tail';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ChowbusInput from './Input';
import useStyles from './InternationalPhoneInput.style';

const PhoneCountryMap = {
  US: {
    dialCode: '1',
    nationName: 'United States',
    abbr: 'USA',
    format: '(...) ...-....',
  },
  CA: {
    dialCode: '1',
    nationName: 'Canada',
    abbr: 'CA',
    format: '(...) ...-....',
  },
};

// TODO this is OTT, just use a string
export const InputVariants = {
  SIGNUP: 'signup',
};

/**
 *
 * @param {object} props
 * @param {'US'|'CA'} props.country
 * @param {boolean} props.error
 * @param {string} props.errorMessage
 * @param {string} props.placeHolder
 * @param {string=} props.phoneNumber
 * @param {string=} props.inputClass
 * @param {string=} props.labelClass
 * @param {string=} props.labelOutlinedClass
 * @param {boolean=} props.showCountryAbbr
 *  * @param {boolean=} props.showCountryOptions
 * @param {function=} props.onChange
 * @param {'signup'=} props.variant
 * @returns
 */
function InternationalPhoneInput(props) {
  const {
    onChange = () => { },
    country = 'US',
    error,
    errorMessage,
    showCountryAbbr = false,
    showCountryOptions = true,
    phoneNumber = '',
    variant,
    name,
    placeHolder,
    inputClass = '',
    labelClass = '',
    labelOutlinedClass = '',
    ...rest
  } = props;
  const [currentCountry, setCurrentCountry] = useState(country);
  const formatterRef = useRef(new AsYouType(currentCountry));
  const classes = useStyles(props);
  const ref = useRef();
  const [phone, setPhone] = useState(phoneNumber);
  const [showOptions, setShowOptions] = useState(false);

  useEffect(() => {
    onNationChange(currentCountry);
  }, [currentCountry]);

  useEffect(() => {
    if (currentCountry !== country) {
      setCurrentCountry(country);
    }
  }, [country]);

  useLayoutEffect(() => {
    const input = ref.current;
    if (!input) {
      return;
    }
    const len = input.value.length;
    if (len > 0) {
      input.setSelectionRange(len, len);
    }
  }, [phone]);

  const onNationChange = useCallback(
    (nation) => {
      const unformattedNumber = phone.replace(/\D/g, '');
      const newFormatter = new AsYouType(nation);
      formatterRef.current = newFormatter;
      const formattedNumber = formatNumber(unformattedNumber, PhoneCountryMap[nation].format);
      handleFormattedPhone(formattedNumber);
      setShowOptions(false);
    },
    [currentCountry]
  );

  const formatNumber = (text, patternArg) => {
    if (!text || !text.length) {
      return '';
    }

    const formattedObject = reduce(
      patternArg,
      (acc, character) => {
        if (acc.remainingText.length === 0) {
          return acc;
        }
        if (character !== '.') {
          return {
            formattedText: acc.formattedText + character,
            remainingText: acc.remainingText,
          };
        }
        return {
          formattedText: acc.formattedText + head(acc.remainingText),
          remainingText: tail(acc.remainingText),
        };
      },
      {
        formattedText: '',
        remainingText: text.split(''),
      }
    );

    let formattedNumber = formattedObject.formattedText + formattedObject.remainingText.join('');
    // Always close brackets
    // if (formattedNumber.includes('(') && !formattedNumber.includes(')')) formattedNumber += ')'
    return formattedNumber;
  };

  const handleFormattedPhone = (formattedNumber) => {
    if (phone !== formattedNumber) {
      setPhone(formattedNumber);
      const formatter = formatterRef.current;
      formatter.reset();
      const dialCode = (PhoneCountryMap[currentCountry] || {}).dialCode || '';
      formatter.input(`+${dialCode} ${formattedNumber}`);
      const number = formatter.getNumber();
      //const e164 = number? number.number : undefined
      onChange({ target: { value: number } });
    }
  };

  const onInputChange = (e) => {
    let newValue = e.target.value || '';
    const { format } = PhoneCountryMap[currentCountry];
    newValue = newValue.replace(/\D/g, '');
    if (newValue.length > 15) {
      return;
    }

    const formattedNumber = formatNumber(newValue, format);
    handleFormattedPhone(formattedNumber);
  };

  const showOptionsPanel = () => {
    setShowOptions(true);
  };

  const hideOptionsPanel = () => {
    setShowOptions(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <ClickAwayListener onClickAway={hideOptionsPanel}>
        <Grid container className={classes.phoneInputWrap}>
          {showCountryOptions && <>
            <Grid item className={classes.phoneInputAddon} onClick={showOptionsPanel}>
              +{(PhoneCountryMap[currentCountry] || {}).dialCode}
              {showCountryAbbr && <> {(PhoneCountryMap[currentCountry] || {}).abbr}</>}
            </Grid>
            {showOptions && (
              <Paper className={classes.nationOptionPanel}>
                <MenuList>
                  {Object.keys(PhoneCountryMap).map((nation) => (
                    <MenuItem
                      key={nation}
                      onClick={() => setCurrentCountry(nation)}
                      selected={nation === currentCountry}>
                      <Grid container className={classes.countryOption}>
                        <Grid item className={classes.phonePrefix}>
                          +{PhoneCountryMap[nation].dialCode}
                        </Grid>
                        <Grid item className={classes.nationName}>
                          {PhoneCountryMap[nation].nationName}
                        </Grid>
                      </Grid>
                    </MenuItem>
                  ))}
                </MenuList>
              </Paper>
            )}
          </>
          }
          <Grid item className={classes.inputContainer}>
            {variant === InputVariants.SIGNUP ? (
              <ChowbusInput
                name={name}
                value={phone}
                onChange={onInputChange}
                margin='none'
                label={placeHolder || 'Phone'}
                error={error}
                errorMessage={errorMessage}
                outlinedClass={showCountryOptions ? classes.outlinedChowbusInput : ''}
                inputClass={inputClass || ''}
                labelClass={labelClass}
                labelOutlinedClass={labelOutlinedClass}
                fullwidth
                type={'tel'}
                style={{ width: '100%', fontSize: '30px' }}
                {...rest}
              />
            ) : (
              <>
                <Input
                  name={name}
                  inputProps={{
                    'aria-label': name,
                    'data-testid': name,
                    name,
                  }}
                  inputRef={ref}
                  disableUnderline
                  value={phone}
                  onChange={onInputChange}
                  fullWidth
                />
                {error && errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}
              </>
            )}
          </Grid>
        </Grid>
      </ClickAwayListener>
    </div>
  );
}

export default InternationalPhoneInput;
